// src/hooks/usePricingStore.ts
import { create } from 'zustand';
import seedPricing from '../data/seed-pricing.json';
import masterDefaultPricing from '../data/master-default-pricing.json';

export type PricingEntry = {
  pricePerM2?: { [supplierId: string]: number };
  minArea?: number;
  poa?: boolean;
  weightPerM2?: number;
};

export type ThicknessLeaf = {
  types: { [typeName: string]: PricingEntry };
};

export type ProductsStructure = {
  Single: {
    [thickness: string]: ThicknessLeaf;
  };
  Double: {
    [thickness: string]: {
      [makeup: string]: ThicknessLeaf;
    };
  };
};

export type ExtrasStructure = {
  [name: string]: {
    flat?: number;
    perKg?: number;
    pricePerM2?: { [supplierId: string]: number };
    poa?: boolean;
    special?: string;
  };
};

export type PricingStructure = {
  products: ProductsStructure;
  extras: ExtrasStructure;
};

type PricingStore = {
  pricing: PricingStructure | null;
  setPricing: (data: PricingStructure) => void;
  resetPricing: () => void;
  restoreDefaultPricing: () => void;
};

// Use seed-pricing.json for default pricing
const defaultPricing: PricingStructure = seedPricing as unknown as PricingStructure;

// Use master-default-pricing.json for factory reset
const factoryDefaultPricing: PricingStructure = masterDefaultPricing as unknown as PricingStructure;

// Helper to migrate old pricing data to new per-supplier format
function migratePricingData(pricing: PricingStructure): PricingStructure {
  const migrateEntry = (entry: any) => {
    if (entry && typeof entry.pricePerM2 === 'number') {
      entry.pricePerM2 = { supplierA: entry.pricePerM2 };
    }
    return entry;
  };

  const migrateTypes = (types: any) => {
    Object.keys(types).forEach(typeName => {
      types[typeName] = migrateEntry(types[typeName]);
    });
    return types;
  };

  const products = { ...pricing.products };
  // Single
  Object.keys(products.Single).forEach(thickness => {
    const leaf = products.Single[thickness];
    if (leaf && leaf.types) {
      leaf.types = migrateTypes(leaf.types);
    }
  });
  // Double
  Object.keys(products.Double).forEach(thickness => {
    const makeups = products.Double[thickness];
    Object.keys(makeups).forEach(makeup => {
      const leaf = makeups[makeup];
      if (leaf && leaf.types) {
        leaf.types = migrateTypes(leaf.types);
      }
    });
  });
  // Extras
  const extras = { ...pricing.extras };
  Object.keys(extras).forEach(extraName => {
    if (extras[extraName]) {
      const price = extras[extraName].pricePerM2;
      if (typeof price === 'number') {
        extras[extraName].pricePerM2 = { supplierA: price };
      } else if (typeof price === 'object' && price !== null) {
        // Already correct format, do nothing
      } else {
        // If undefined or other, set to empty object
        extras[extraName].pricePerM2 = {};
      }
    }
  });
  return { ...pricing, products, extras };
}

// Initialize pricing from localStorage or defaults
const getInitialPricing = (): PricingStructure => {
  try {
    const savedData = localStorage.getItem('pricingData');
    if (savedData) {
      return migratePricingData(JSON.parse(savedData)) as unknown as PricingStructure;
    }
  } catch (error) {
    console.error('Error loading pricing data from localStorage:', error);
    localStorage.removeItem('pricingData');
  }
  return migratePricingData(defaultPricing) as unknown as PricingStructure;
};

export const usePricingStore = create<PricingStore>((set) => ({
  pricing: getInitialPricing(),
  setPricing: (data) => {
    localStorage.setItem('pricingData', JSON.stringify(data));
    set({ pricing: data });
  },
  resetPricing: () => {
    localStorage.removeItem('pricingData');
    set({ pricing: defaultPricing });
  },
  restoreDefaultPricing: () => {
    // Replace current pricing with the master defaults
    localStorage.setItem('pricingData', JSON.stringify(factoryDefaultPricing));
    set({ pricing: factoryDefaultPricing });
  }
}));
